import type {
  ClockProps, CountdownProps, RamadanProps, DonationQrProps, DonationsProps, EventsProps, JumuahProps, PrayerState, PrayerTimesProps, ScriptureProps, TickerProps, AnnouncementsProps,
} from '@noorview/core';
import {
  scriptureFor, presetOf, PRAYER_LABEL, addDays, designOf, autoInk, hexToRgb, rgbToHex, clockDuration, compactDuration, dailyPick, formatDate, formatHijri, hhmm, hijriFor, nextJumuah, parseYmd, ymd, zonedParts, zonedToInstant,
} from '@noorview/core';
import { el, minutesOf, setText, sv, t12, wall12 } from './dom';
import type { Env, Tick, WidgetView } from './widgets';

/**
 * Alternative designs for widgets: analog and flip clocks, prayer times with icons or clock faces, an hourglass,
 * a thermometer and more. Every design draws with the widget's own colours (--ink for text, --a for highlights,
 * --tile for inner surfaces), so styles, colours and fonts apply to all of them.
 *
 * Animation only ever changes transform and opacity, so it runs on the graphics chip without layout work.
 */

/* ---------- shared pieces ---------- */

type Key = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha' | 'tarawih';
const PRAYER_KEYS = (sunrise: boolean, tarawih = false): Key[] => ['fajr', ...(sunrise ? (['sunrise'] as Key[]) : []), 'dhuhr', 'asr', 'maghrib', 'isha', ...(tarawih ? (['tarawih'] as Key[]) : [])];
/** Tarawih joins the list on days the timetable gives a time for it (Ramadan), unless the widget hides it. */
const wantsTarawih = (p: PrayerTimesProps, state: PrayerState) => p.showTarawih !== false && !!state.today.tarawih;
const ARABIC_DIGITS = ['١٢', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩', '١٠', '١١'];

/** Rotates part of an SVG drawing around the dial's centre (100, 100). */
const turn = (g: SVGElement, deg: number) => { (g as SVGGElement).style.transform = `rotate(${deg.toFixed(2)}deg)`; };

/** An analog dial. `kind` sets the face; the second hand sweeps smoothly with one CSS animation (no per-frame work). */
function dial(kind: 'classic' | 'minimal' | 'arabic' | 'mini') {
  const root = sv('svg', { viewBox: '0 0 200 200', class: `dial dial-${kind}`, 'aria-hidden': 'true' });
  if (kind !== 'minimal') root.append(sv('circle', { cx: 100, cy: 100, r: 96, class: 'dial-face' }));
  else root.append(sv('circle', { cx: 100, cy: 100, r: 96, class: 'dial-ring' }));
  const ticks = sv('g', { class: 'dial-ticks' });
  const tickCount = kind === 'classic' ? 60 : 12;
  for (let i = 0; i < tickCount; i++) {
    const major = kind !== 'classic' || i % 5 === 0;
    const len = kind === 'mini' ? 12 : kind === 'classic' ? (major ? 9 : 5) : 14;
    const t = sv('rect', { x: major ? 98 : 99.2, y: 8, width: major ? 4 : 1.6, height: len, rx: major ? 2 : 0.8, class: major ? 'tk-major' : 'tk-minor' });
    t.setAttribute('transform', `rotate(${(i * 360) / tickCount} 100 100)`);
    ticks.append(t);
  }
  if (kind !== 'arabic') root.append(ticks);
  if (kind === 'classic' || kind === 'arabic') {
    const nums = sv('g', { class: `dial-nums${kind === 'arabic' ? ' ar' : ''}` });
    for (let i = 0; i < 12; i++) {
      const a = (i * Math.PI) / 6, r = kind === 'arabic' ? 74 : 66;
      const tx = sv('text', { x: (100 + Math.sin(a) * r).toFixed(1), y: (100 - Math.cos(a) * r).toFixed(1), 'text-anchor': 'middle', 'dominant-baseline': 'central' });
      tx.textContent = kind === 'arabic' ? ARABIC_DIGITS[i]! : String(i === 0 ? 12 : i);
      nums.append(tx);
    }
    root.append(nums);
    if (kind === 'arabic') {
      const dots = sv('g', { class: 'dial-ticks' });
      for (let i = 0; i < 60; i++) if (i % 5) { const d = sv('circle', { cx: 100, cy: 7, r: 1.1, class: 'tk-minor' }); d.setAttribute('transform', `rotate(${i * 6} 100 100)`); dots.append(d); }
      root.append(dots);
    }
  }
  const hour = sv('g', { class: 'hand' }, sv('rect', { x: 95.5, y: kind === 'mini' ? 52 : 48, width: 9, height: kind === 'mini' ? 56 : 60, rx: 4.5, class: 'hand-h' }));
  const minute = sv('g', { class: 'hand' }, sv('rect', { x: 97, y: kind === 'mini' ? 26 : 20, width: 6, height: kind === 'mini' ? 82 : 88, rx: 3, class: 'hand-m' }));
  const second = sv('g', { class: 'hand hand-sweep' }, sv('rect', { x: 99, y: 14, width: 2, height: 108, rx: 1, class: 'hand-s' }), sv('circle', { cx: 100, cy: 100, r: 5.5, class: 'hand-s' }));
  root.append(hour, minute, second, sv('circle', { cx: 100, cy: 100, r: 3.2, class: 'dial-pin' }));
  return {
    root,
    /** Shows a time (hours and minutes as numbers; seconds only for a live clock). */
    set(h: number, m: number, s: number | null) {
      turn(hour, ((h % 12) + m / 60) * 30);
      turn(minute, (m + (s ?? 0) / 60) * 6);
      second.style.display = s == null ? 'none' : '';
    },
    /** Starts (or re-syncs) the smooth second hand. */
    sweep(secondsIntoMinute: number) {
      second.style.animationDelay = `${-secondsIntoMinute.toFixed(3)}s`;
    },
  };
}

/** A flip-card number (split-flap). Changing the value folds the old top half down onto the new one. */
function flipDigit() {
  const root = el('div', 'fl');
  const top = el('div', 'fl-h fl-top'), bot = el('div', 'fl-h fl-bot'), ft = el('div', 'fl-h fl-top fl-ft'), fb = el('div', 'fl-h fl-bot fl-fb');
  const span = (p: HTMLElement) => { const s = el('span'); p.append(s); return s; };
  const [t, b, f1, f2] = [span(top), span(bot), span(ft), span(fb)];
  root.append(top, bot, ft, fb);
  let cur = '';
  fb.addEventListener('animationend', () => { b.textContent = cur; root.classList.remove('go'); });
  return {
    root,
    set(v: string, animate: boolean) {
      if (v === cur) return;
      const old = cur;
      cur = v;
      if (!animate || !old) { t.textContent = b.textContent = f1.textContent = f2.textContent = v; return; }
      f1.textContent = old; b.textContent = old; t.textContent = v; f2.textContent = v;
      root.classList.remove('go');
      void root.offsetWidth; // restart the fold
      root.classList.add('go');
    },
  };
}

function flipGroup(parent: HTMLElement, groups: number) {
  const digits: Array<ReturnType<typeof flipDigit>> = [];
  for (let g = 0; g < groups; g++) {
    if (g) parent.append(el('span', 'fl-colon', ':'));
    for (let i = 0; i < 2; i++) { const d = flipDigit(); digits.push(d); parent.append(d.root); }
  }
  return (text: string, animate: boolean) => { const chars = text.replace(/\D/g, '').padStart(groups * 2, '0').slice(-groups * 2); digits.forEach((d, i) => d.set(chars[i]!, animate)); };
}

/** Prayer icons drawn as simple line art (stroke = text colour, accents = highlight). */
export function prayerIcon(k: Key): SVGSVGElement {
  const s = sv('svg', { viewBox: '0 0 48 48', class: `pi pi-${k}`, 'aria-hidden': 'true', fill: 'none', 'stroke-width': 3, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' });
  const p = (d: string, cls = '') => s.append(sv('path', { d, class: cls }));
  const horizon = () => p('M6 34h36');
  switch (k) {
    case 'fajr': horizon(); p('M14 34a10 10 0 0 1 20 0', 'pi-sun'); p('M10 42h28'); s.append(sv('circle', { cx: 35, cy: 12, r: 1.8, class: 'pi-star' })); s.append(sv('circle', { cx: 13, cy: 15, r: 1.4, class: 'pi-star' })); break;
    case 'sunrise': horizon(); p('M14 34a10 10 0 0 1 20 0', 'pi-sun'); p('M24 10v6M12 20l4 4M36 20l-4 4M24 41v-5M20 39l4-3 4 3'); break;
    case 'dhuhr': s.append(sv('circle', { cx: 24, cy: 24, r: 8, class: 'pi-sun' })); p('M24 6v5M24 37v5M6 24h5M37 24h5M11.3 11.3l3.5 3.5M33.2 33.2l3.5 3.5M11.3 36.7l3.5-3.5M33.2 14.8l3.5-3.5'); break;
    case 'asr': s.append(sv('circle', { cx: 20, cy: 18, r: 7, class: 'pi-sun' })); p('M20 5v3M7 18h3M10.8 8.8l2.1 2.1M29.2 8.8l-2.1 2.1'); p('M14 38h20a7 7 0 0 0 0-14 9 9 0 0 0-17 3 5.5 5.5 0 0 0-3 11z'); break;
    case 'maghrib': horizon(); p('M14 34a10 10 0 0 1 20 0', 'pi-sun'); p('M24 10v7M20 13l4 4 4-4M10 42h28'); break;
    case 'isha': p('M32 30A13 13 0 0 1 22 8a14 14 0 1 0 18 17 13 13 0 0 1-8 5z', 'pi-moon'); s.append(sv('path', { d: 'M36 8l1.2 2.8L40 12l-2.8 1.2L36 16l-1.2-2.8L32 12l2.8-1.2z', class: 'pi-star' })); break;
    case 'tarawih':
      // A lantern beneath a crescent.
      p('M30 6a7 7 0 1 0 6 10 5.5 5.5 0 0 1-6-10z', 'pi-moon');
      p('M18 20h12M20 20l-2 6v12l2 4h8l2-4V26l-2-6M18 26h12M18 38h12M24 14v6');
      break;
  }
  return s;
}

const isJumuahDhuhr = (state: PrayerState, k: Key) => k === 'dhuhr' && state.today.isFriday && state.today.jumuah.length > 0;
const jamaahOf = (state: PrayerState, k: Key) => (k === 'sunrise' || k === 'tarawih' ? undefined : isJumuahDhuhr(state, k) ? state.today.jumuah[0]!.jamaah : state.today.jamaah[k]);
/** The jama'ah line: on Fridays both Jumu'ahs (1st · 2nd) when there are two. */
const jamText = (state: PrayerState, k: Key, h12: boolean) => (k === 'sunrise' || k === 'tarawih' ? '—'
  : isJumuahDhuhr(state, k) ? state.today.jumuah.map((j) => wall12(j.jamaah, h12)).join(' · ') : wall12(jamaahOf(state, k), h12));
const nameOf = (state: PrayerState, k: Key) => (k === 'tarawih' ? 'Tarawih' : isJumuahDhuhr(state, k) ? 'Jumu‘ah' : PRAYER_LABEL[k]);
const startOf = (state: PrayerState, k: Key) => (k === 'tarawih' ? state.today.tarawih ?? '' : state.today.start[k]);
function nextLine(state: PrayerState, now: Date, day: string) {
  if (state.empty) return { label: 'Upload a timetable', in: '—', at: now };
  const j = state.nextJamaah, s = state.nextStart;
  const useJ = j && state.current && j.prayer === state.current && j.at.getTime() < s.at.getTime();
  const m = useJ ? j! : s;
  const label = m.prayer === 'dhuhr' && state.today.isFriday && m.day === day && state.today.jumuah.length ? 'Jumu‘ah' : PRAYER_LABEL[m.prayer];
  return { label: `${label}${useJ ? ' jama‘ah' : ''}`, in: compactDuration(m.at.getTime() - now.getTime()), at: m.at };
}

/* ---------- Clock designs ---------- */

export function analogClock(c: HTMLElement, kind: 'classic' | 'minimal' | 'arabic'): WidgetView {
  let p: ClockProps;
  const wrap = el('div', 'ck-analog');
  const d = dial(kind);
  const box = el('div', 'dial-box');
  box.append(d.root);
  const meta = el('div', 'ck-meta'), date = el('div', 'ck-date'), hijri = el('div', 'ck-hijri');
  meta.append(date, hijri);
  wrap.append(box, meta);
  c.append(wrap);
  let lastMin = -1;
  return {
    update(w, env) {
      p = w.props as ClockProps;
      date.style.display = p.showDate ? '' : 'none';
      hijri.style.display = p.showHijri ? '' : 'none';
      meta.style.display = p.showDate || p.showHijri ? '' : 'none';
      wrap.classList.toggle('side', env.box.width / env.box.height > 1.6);
      lastMin = -1;
    },
    tick({ now, day }, env) {
      const z = zonedParts(now, env.masjid.timezone);
      d.set(z.hour, z.minute, p.showSeconds ? z.second : null);
      if (p.showSeconds && z.minute !== lastMin) { lastMin = z.minute; d.sweep(z.second + now.getMilliseconds() / 1000); }
      let ch = false;
      if (p.showDate) ch = setText(date, formatDate(now, env.masjid.timezone, 'long')) || ch;
      if (p.showHijri) ch = setText(hijri, formatHijri(hijriFor(day, env.masjid.prayer.hijriAdjust))) || ch;
      return ch;
    },
    destroy() {},
  };
}

export function flipClock(c: HTMLElement): WidgetView {
  let p: ClockProps;
  const wrap = el('div', 'nvd-center');
  const row = el('div', 'fl-row');
  const date = el('div', 'ck-date'), hijri = el('div', 'ck-hijri');
  wrap.append(row, date, hijri);
  c.append(wrap);
  let set = flipGroup(row, 2), groups = 2, first = true;
  return {
    update(w) {
      p = w.props as ClockProps;
      const g = p.showSeconds ? 3 : 2;
      if (g !== groups) { groups = g; row.replaceChildren(); set = flipGroup(row, g); first = true; }
      date.style.display = p.showDate ? '' : 'none';
      hijri.style.display = p.showHijri ? '' : 'none';
    },
    tick({ now, day }, env) {
      const tz = env.masjid.timezone;
      const z = zonedParts(now, tz);
      const h = p.h12 ? (z.hour % 12) || 12 : z.hour;
      set(`${String(h).padStart(2, '0')}${String(z.minute).padStart(2, '0')}${p.showSeconds ? String(z.second).padStart(2, '0') : ''}`, !first && env.mode !== 'thumbnail');
      first = false;
      let ch = false;
      if (p.showDate) ch = setText(date, formatDate(now, tz, 'long')) || ch;
      if (p.showHijri) ch = setText(hijri, formatHijri(hijriFor(day, env.masjid.prayer.hijriAdjust))) || ch;
      return ch;
    },
    destroy() {},
  };
}

export function stackedClock(c: HTMLElement): WidgetView {
  let p: ClockProps;
  const wrap = el('div', 'nvd-center ck-stack');
  const h = el('div', 'st-h'), m = el('div', 'st-m'), sec = el('div', 'st-sec'), date = el('div', 'ck-date'), hijri = el('div', 'ck-hijri');
  wrap.append(h, m, sec, date, hijri);
  c.append(wrap);
  return {
    update(w) {
      p = w.props as ClockProps;
      sec.style.display = p.showSeconds ? '' : 'none';
      date.style.display = p.showDate ? '' : 'none';
      hijri.style.display = p.showHijri ? '' : 'none';
    },
    tick({ now, day }, env) {
      const tz = env.masjid.timezone;
      const [hh, mm] = t12(now, tz, p.h12).split(':');
      let ch = setText(h, (hh ?? '').padStart(2, '0'));
      ch = setText(m, mm ?? '') || ch;
      if (p.showSeconds) setText(sec, String(zonedParts(now, tz).second).padStart(2, '0'));
      if (p.showDate) ch = setText(date, formatDate(now, tz, 'short')) || ch;
      if (p.showHijri) ch = setText(hijri, formatHijri(hijriFor(day, env.masjid.prayer.hijriAdjust))) || ch;
      return ch;
    },
    destroy() {},
  };
}

/* ---------- Prayer time designs ---------- */

interface PtItem { key: Key; root: HTMLElement; n: HTMLElement; s: HTMLElement; j: HTMLElement; clock?: ReturnType<typeof dial> }

/** Shared base for prayer-time designs: builds one item per prayer, fills times once a day, highlights the next. */
export function prayerBase(c: HTMLElement, cls: string, build0: (k: Key, item: PtItem, p: PrayerTimesProps) => void, opts: { head?: boolean } = {}) {
  const head = el('div', 'pt-head'), title = el('div', 'nvd-label'), next = el('div', 'nvd-label nvd-acc');
  head.append(title, next);
  const grid = el('div', `pt2 ${cls}`), eid = el('div', 'pt-eid');
  c.append(head, grid, eid);
  let p: PrayerTimesProps, items: PtItem[] = [], dayKey = '', sig = '', tarawih = false;
  const build = () => {
    const keys = PRAYER_KEYS(p.showSunrise, tarawih);
    const s = keys.join() + p.showJamaah;
    if (s !== sig) {
      sig = s;
      grid.replaceChildren();
      items = keys.map((key) => {
        const item: PtItem = { key, root: el('div', 'pt2-i'), n: el('div', 'pt-n'), s: el('div', 'pt-s'), j: el('div', 'pt-j') };
        build1(key, item, p);
        grid.append(item.root);
        return item;
      });
    }
    for (const it of items) {
      const own = it.key === 'tarawih' ? undefined : p.prayerColors?.[it.key];
      const bg = own?.tile || p.tileColor, ink = own?.text || p.tileText || (bg ? rgbToHex(autoInk(hexToRgb(bg))) : null);
      it.root.style.setProperty('--pt-bg', bg || '');
      if (!bg) it.root.style.removeProperty('--pt-bg');
      if (ink) it.root.style.setProperty('--pt-ink', ink); else it.root.style.removeProperty('--pt-ink');
      it.root.classList.toggle('own', !!own?.tile);
      it.j.style.display = p.showJamaah ? '' : 'none';
    }
  };
  const build1 = build0;
  const view: WidgetView = {
    update(w, env) {
      // Shape follows the widget: a list when it's tall, a 3 × 2 grid when it's about square, one row when it's wide.
      const r = env.box.width / Math.max(1, env.box.height);
      grid.dataset.shape = r < 0.95 ? 'list' : r < 1.9 ? 'grid' : 'row';
      p = w.props as PrayerTimesProps;
      title.textContent = p.title || 'Prayer Times';
      head.style.display = opts.head !== false && (p.title || p.showNext) ? '' : 'none';
      next.style.display = p.showNext ? '' : 'none';
      grid.classList.toggle('cards', !!p.cards);
      build();
      dayKey = '';
    },
    tick({ now, day, state }) {
      let changed = false;
      if (day !== dayKey) {
        dayKey = day;
        const tw = wantsTarawih(p, state);
        if (tw !== tarawih) { tarawih = tw; build(); changed = true; }
        for (const it of items) {
          changed = setText(it.n, nameOf(state, it.key)) || changed;
          const start = startOf(state, it.key);
          changed = setText(it.s, wall12(start, p.h12)) || changed;
          changed = setText(it.j, jamText(state, it.key, p.h12)) || changed;
          // The jama'ah is the big time; without one, the start time is.
          it.root.classList.toggle('no-jam', !p.showJamaah || !jamaahOf(state, it.key));
          if (it.clock) { const m = start ? minutesOf(start) : null; if (m != null && Number.isFinite(m)) it.clock.set(Math.floor(m / 60), m % 60, null); }
        }
        const e = state.today.eid;
        eid.style.display = e ? '' : 'none';
        if (e) setText(eid, `${e.name}: ${e.takbeer ? `Takbeer ${wall12(e.takbeer, p.h12)} · ` : ''}Salah ${e.jamaats.map((j) => wall12(j, p.h12)).join(', ')}`);
      }
      for (const it of items) it.root.classList.toggle('on', !state.empty && it.key === state.highlight);
      if (p.showNext) { const n = nextLine(state, now, day); changed = setText(next, state.empty ? n.label : `${n.label} in ${n.in}`) || changed; }
      return changed;
    },
    destroy() {},
  };
  return view;
}

export const prayerIcons = (c: HTMLElement) => prayerBase(c, 'pt-icons', (k, it) => {
  const ic = el('div', 'pt2-ic'); ic.append(prayerIcon(k));
  it.root.append(ic, it.n, it.s, it.j);
});

export const prayerClocks = (c: HTMLElement) => prayerBase(c, 'pt-clocks', (_k, it) => {
  const d = dial('mini'); it.clock = d;
  const box = el('div', 'pt2-dial'); box.append(d.root);
  it.root.append(box, it.n, it.s, it.j);
});

export function prayerTable(c: HTMLElement): WidgetView {
  const hdr = el('div', 'pt2-hdr');
  hdr.append(el('span', ''), el('span', 'nvd-label', 'Begins'), el('span', 'nvd-label jam-h', 'Jama‘ah'));
  const v = prayerBase(c, 'pt-table', (k, it) => { const ic = el('span', 'pt2-tic'); ic.append(prayerIcon(k)); const nm = el('span', 'pt2-name'); nm.append(ic, it.n); it.root.append(nm, it.s, it.j); });
  const grid = c.querySelector('.pt2')!;
  grid.before(hdr);
  const up = v.update;
  // A table always reads as rows, whatever the widget's shape.
  v.update = (w, env) => { up(w, env); (grid as HTMLElement).dataset.shape = 'list'; const p = w.props as PrayerTimesProps; (hdr.querySelector('.jam-h') as HTMLElement).style.display = p.showJamaah ? '' : 'none'; hdr.classList.toggle('no-j', !p.showJamaah); grid.classList.toggle('no-j', !p.showJamaah); };
  return v;
}

/** Each salah's name in Arabic, for the Board design. */
const ARABIC: Record<Key, string> = { fajr: 'الفجر', sunrise: 'الشروق', dhuhr: 'الظهر', asr: 'العصر', maghrib: 'المغرب', isha: 'العشاء', tarawih: 'التراويح' };

/**
 * The classic masjid board: a row for every salah with its English and Arabic name, the time it begins, and the
 * jama'ah time large. The times can have their own font and colour (e.g. green seven-segment digits on black).
 */
export function prayerBoard(c: HTMLElement): WidgetView {
  const hdr = el('div', 'pt2-hdr pb-hdr');
  hdr.append(el('span', ''), el('span', 'nvd-label', 'Begins'), el('span', 'nvd-label jam-h', 'Jama‘ah'));
  const ar = new Map<Key, HTMLElement>();
  // Two Jumu'ahs ("13:15 · 14:00") don't fit one board cell, so they're shown one above the other instead.
  const jum = new Map<Key, { j: HTMLElement; two: HTMLElement }>();
  const v = prayerBase(c, 'pt-board', (k, it) => {
    const nm = el('span', 'pb-name'), a = el('span', 'pb-ar', ARABIC[k]);
    a.lang = 'ar'; a.dir = 'rtl';
    ar.set(k, a);
    nm.append(it.n, a);
    const two = el('span', 'pt-j pb-two');
    two.style.display = 'none';
    jum.set(k, { j: it.j, two });
    it.root.append(nm, it.s, it.j, two);
  });
  const grid = c.querySelector('.pt2') as HTMLElement;
  grid.before(hdr);
  const up = v.update, tk = v.tick;
  v.update = (w, env) => {
    up(w, env);
    grid.dataset.shape = 'list';
    // The times grow with their rows (CSS), so a tall portrait board fills its height.
    const p = w.props as PrayerTimesProps;
    (hdr.querySelector('.jam-h') as HTMLElement).style.display = p.showJamaah ? '' : 'none';
    hdr.classList.toggle('no-j', !p.showJamaah);
    grid.classList.toggle('no-j', !p.showJamaah);
    grid.classList.toggle('no-ar', p.showArabic === false);
  };
  v.tick = (t, env) => {
    let ch = tk(t, env);
    // On Fridays the Dhuhr row is Jumu'ah.
    const d = ar.get('dhuhr');
    if (d) setText(d, /jum/i.test(d.parentElement?.firstChild?.textContent ?? '') ? 'الجمعة' : ARABIC.dhuhr);
    for (const { j, two } of jum.values()) {
      const parts = (j.textContent ?? '').split(' · ');
      const many = parts.length > 1;
      if (many !== (two.style.display !== 'none')) { two.style.display = many ? '' : 'none'; j.style.display = many ? 'none' : ''; ch = true; }
      if (many && two.dataset.v !== j.textContent) {
        two.dataset.v = j.textContent ?? '';
        two.replaceChildren(...parts.map((x) => el('span', '', x)));
        ch = true;
      }
    }
    return ch;
  };
  return v;
}

/** The next prayer, large, with the day's other times beside or below it. */
export function prayerFocus(c: HTMLElement): WidgetView {
  const wrap = el('div', 'pf');
  const hero = el('div', 'pf-hero'), hl = el('div', 'nvd-label'), hn = el('div', 'pf-name'), ht = el('div', 'pf-time'), hj = el('div', 'pf-begins'), hc = el('div', 'pf-in nvd-acc');
  const ic = el('div', 'pf-ic');
  hero.append(ic, hl, hn, ht, hj, hc);
  const rest = el('div', 'pf-rest');
  wrap.append(hero, rest);
  c.append(wrap);
  let p: PrayerTimesProps, rows: Array<{ key: Key; root: HTMLElement; n: HTMLElement; s: HTMLElement; j: HTMLElement }> = [], dayKey = '', sig = '', heroKey = '', tarawih = false;
  return {
    update(w, env) {
      p = w.props as PrayerTimesProps;
      wrap.classList.toggle('side', env.box.width / env.box.height > 1.3);
      hl.textContent = 'Next prayer';
      rebuild();
      for (const r of rows) r.j.style.display = p.showJamaah ? '' : 'none';
      dayKey = ''; heroKey = '';
    },
    tick({ now, day, state }) {
      let ch = false;
      if (day !== dayKey) {
        dayKey = day;
        const tw = wantsTarawih(p, state);
        if (tw !== tarawih) { tarawih = tw; rebuild(); for (const r of rows) r.j.style.display = p.showJamaah ? '' : 'none'; }
        for (const r of rows) {
          setText(r.n, nameOf(state, r.key));
          setText(r.s, wall12(startOf(state, r.key), p.h12));
          setText(r.j, jamText(state, r.key, p.h12));
          r.root.classList.toggle('no-jam', !p.showJamaah || !jamaahOf(state, r.key));
        }
        ch = true;
      }
      return tickRest(now, day, state, ch);
    },
    destroy() {},
  };
  function rebuild() {
      const keys = PRAYER_KEYS(p.showSunrise, tarawih);
      if (keys.join() !== sig) {
        sig = keys.join();
        rows = keys.map((key) => { const root = el('div', 'pf-r'), n = el('span', 'pt-n'), s = el('span', 'pt-s'), j = el('span', 'pt-j'); root.append(n, s, j); return { key, root, n, s, j }; });
        rest.replaceChildren(...rows.map((r) => r.root));
      }
  }
  function tickRest(now: Date, day: string, state: PrayerState, ch0: boolean) {
      let ch = ch0;
      const k = state.highlight as Key;
      for (const r of rows) r.root.classList.toggle('on', !state.empty && r.key === k);
      if (heroKey !== k + day) {
        heroKey = k + day;
        ic.replaceChildren(prayerIcon(k));
        ch = setText(hn, nameOf(state, k)) || ch;
        // The jama'ah time is the big one; the start time sits under it.
        const j = p.showJamaah ? jamaahOf(state, k) : undefined;
        ch = setText(ht, j ? jamText(state, k, p.h12) : wall12(startOf(state, k), p.h12)) || ch;
        hj.style.display = j ? '' : 'none';
        ch = setText(hj, j ? `Begins ${wall12(startOf(state, k), p.h12)}` : '') || ch;
      }
      const n = nextLine(state, now, day);
      ch = setText(hc, state.empty ? n.label : `${n.label} in ${n.in}`) || ch;
      return ch;
  }
}

/* ---------- Countdown designs ---------- */

/** Target and a sensible "from" moment, so rings and hourglasses can show how much time has passed. */
export function countdownTarget(p: CountdownProps, t: Tick, env: Env) {
  const { now, state, day } = t;
  if (p.target === 'custom') {
    const at = p.customAt ? new Date(p.customAt) : null;
    if (!at || !Number.isFinite(at.getTime())) return null;
    // Rings and hourglasses show the final week (or day, when it's closer than a week).
    const span = at.getTime() - now.getTime() > 86400_000 ? 7 * 86400_000 : 86400_000;
    return { at, label: p.label || p.customLabel || 'Event', from: new Date(Math.min(now.getTime(), at.getTime() - span)) };
  }
  if (state.empty) return null;
  const m = p.target === 'nextJamaah' ? state.nextJamaah ?? state.nextStart : state.nextStart;
  const jum = m.prayer === 'dhuhr' && state.today.isFriday && m.day === day && state.today.jumuah.length > 0;
  // Count from the most recent prayer start before now (before Fajr, from about last night's Isha).
  let from: Date | null = null;
  for (const k of ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'] as const) {
    if (!state.today.start[k]) continue;
    const at = zonedToInstant(day, state.today.start[k], env.masjid.timezone);
    if (at.getTime() <= now.getTime() && (!from || at.getTime() > from.getTime())) from = at;
  }
  if (!from) from = new Date(Math.min(now.getTime(), m.at.getTime() - 8 * 3600_000));
  return { at: m.at, from, label: `${jum ? 'Jumu‘ah' : PRAYER_LABEL[m.prayer]}${m.kind === 'jamaah' ? ' jama‘ah' : ''} in` };
}

/* ---------- Donation designs ---------- */

const money = (n: number, cur: string) => `${cur || '£'}${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(Math.max(0, n))}`;
/** Colour for a percentage: cool blue when an appeal starts, warming through green and gold to a glowing red. */
export function heatColour(pct: number): string {
  const stops: Array<[number, [number, number, number]]> = [[0, [10, 132, 255]], [0.35, [48, 209, 88]], [0.7, [255, 214, 10]], [1, [255, 69, 58]]];
  const x = Math.max(0, Math.min(1, pct));
  for (let i = 1; i < stops.length; i++) {
    const [p1, c1] = stops[i]!, [p0, c0] = stops[i - 1]!;
    if (x <= p1) { const t = (x - p0) / (p1 - p0); return `rgb(${c0.map((v, k) => Math.round(v + (c1[k]! - v) * t)).join(',')})`; }
  }
  return 'rgb(255,69,58)';
}

function donationNumbers(p: DonationsProps) {
  const raised = Math.max(0, Number(p.raised) || 0), goal = Math.max(0, Number(p.goal) || 0);
  return { raised, goal, pct: goal > 0 ? Math.min(1, raised / goal) : 0 };
}

/* ---------- Jumu'ah: big times ---------- */

export function jumuahFocus(c: HTMLElement): WidgetView {
  let p: JumuahProps;
  const wrap = el('div', 'nvd-center');
  const label = el('div', 'nvd-label'), row = el('div', 'jf-row'), sub = el('div', 'nvd-sub nvd-acc');
  wrap.append(label, row, sub);
  c.append(wrap);
  let key = '';
  return {
    update(w, env) {
      p = w.props as JumuahProps;
      row.classList.toggle('stack', env.box.width / Math.max(1, env.box.height) < 1);
      label.textContent = p.title || 'Jumu‘ah';
      key = '';
    },
    tick({ now }, env) {
      // Next Friday's Jumu'ah: the timetable's Jumu'ah 1 and 2, or the khutbah and jama'ah from settings.
      const n = nextJumuah(env.masjid, env.timetable, now);
      let ch = false;
      const k = n ? n.day + JSON.stringify(n.slots) : '';
      if (k !== key) {
        key = k; ch = true;
        row.replaceChildren();
        if (!n) { row.append(el('div', 'nvd-sub', env.mode === 'screen' ? '' : 'Add Jumu‘ah times to your timetable')); setText(sub, ''); return true; }
        const boxes: Array<[string, string]> = n.slots.length > 1
          ? n.slots.map((s, i) => [i === 0 ? '1st Jumu‘ah' : '2nd Jumu‘ah', s.jamaah])
          : n.slots[0]!.khutbah ? [['Khutbah', n.slots[0]!.khutbah], ['Jama‘ah', n.slots[0]!.jamaah]] : [['Jama‘ah', n.slots[0]!.jamaah]];
        for (const [name, v] of boxes) { const b = el('div', `jf-b${name === 'Khutbah' ? ' minor' : ''}`); b.append(el('div', 'pt-n', name), el('div', 'big', v)); row.append(b); }
      }
      if (!n) return ch;
      const at = zonedToInstant(n.day, n.slots[0]!.jamaah, env.masjid.timezone);
      const left = at.getTime() - now.getTime();
      const today = n.day === ymd(now, env.masjid.timezone);
      return setText(sub, left <= 0 ? 'Now' : today ? `Today · in ${compactDuration(left)}` : `In ${compactDuration(left)}`) || ch;
    },
    destroy() {},
  };
}

/* ---------- Events designs ---------- */

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export function eventsDesign(c: HTMLElement, kind: 'cards' | 'timeline'): WidgetView {
  const label = el('div', 'nvd-label'), list = el('div', kind === 'cards' ? 'evc' : 'evt');
  c.append(label, list);
  let p: EventsProps, key = '', mode: Env['mode'] = 'screen';
  const render = (day: string) => {
    const items = (p.items ?? []).filter((e) => e.title && e.date >= day).sort((a, b) => (a.date + (a.time ?? '')).localeCompare(b.date + (b.time ?? ''))).slice(0, Math.max(1, p.max || 4));
    list.replaceChildren(...items.map((e) => {
      const { month, day: d, year } = parseYmd(e.date);
      const wd = WEEK[new Date(Date.UTC(year, month - 1, d)).getUTCDay()]!;
      const meta = [e.time, e.place].filter(Boolean).join(' · ') || (e.date === day ? 'Today' : '');
      if (kind === 'cards') {
        const card = el('div', 'evc-i'), top = el('div', 'evc-d');
        top.append(el('small', '', `${wd} ${MON[month - 1] ?? ''}`), el('b', '', String(d)));
        card.append(top, el('div', 'ev-t', e.title), el('div', 'ev-m', meta));
        return card;
      }
      const row = el('div', 'evt-i'), dot = el('i', 'evt-dot'), body = el('div', 'nvd-grow');
      body.append(el('div', 'evt-when nvd-acc', e.date === day ? 'Today' : `${wd} ${d} ${MON[month - 1] ?? ''}`), el('div', 'ev-t', e.title), el('div', 'ev-m', meta));
      row.append(dot, body);
      return row;
    }));
    list.classList.toggle('empty', !items.length);
    if (!items.length) list.append(el('div', 'nvd-sub nvd-faint', mode === 'screen' ? '' : 'Add events in the inspector'));
  };
  return {
    update(w, env) { p = w.props as EventsProps; mode = env.mode; label.textContent = p.title || 'Upcoming'; key = ''; },
    tick({ day }) { if (day === key) return false; key = day; render(day); return true; },
    destroy() {},
  };
}

/* ---------- Scripture designs ---------- */

export function scriptureDesign(c: HTMLElement, kind: 'ayah' | 'hadith', design: 'quote' | 'minimal'): WidgetView {
  const wrap = el('div', design === 'quote' ? 'sq' : 'nvd-center');
  const mark = el('div', 'sq-mark', '“'), label = el('div', 'nvd-label'), ar = el('div', 'tx-ar'), tx = el('div', 'tx'), ref = el('div', 'nvd-sub nvd-acc');
  if (design === 'quote') wrap.append(mark, label, ar, tx, ref); else wrap.append(ar, tx, ref);
  c.append(wrap);
  let p: ScriptureProps, key = '';
  return {
    update(w) { p = w.props as ScriptureProps; label.textContent = p.title || (kind === 'ayah' ? 'Ayah of the Day' : 'Hadith'); key = ''; },
    tick({ day }) {
      if (day === key) return false;
      key = day;
      const pick = scriptureFor(kind, p, day);
      setText(tx, pick.text ?? '');
      const r = [p.showNarrator !== false ? pick.narrator : '', pick.reference].filter(Boolean).join(' · ');
      setText(ref, r ? `— ${r}` : '');
      // Text only shows just the words (no Arabic, no heading).
      const showAr = design !== 'minimal' && !!pick.arabic && p.showArabic !== false;
      ar.style.display = showAr ? '' : 'none';
      if (showAr) setText(ar, pick.arabic!);
      return true;
    },
    destroy() {},
  };
}

/* ---------- Announcements as a list or grid (Apple style) ---------- */

/**
 * List: rows like an Apple Reminders or Calendar list. A coloured dot (the widget's accent), the title, the details
 * underneath, and hairline separators that start where the text starts.
 */
export function announcementsList(c: HTMLElement): WidgetView {
  const label = el('div', 'nvd-label ann-h lead'), list = el('div', 'anl');
  c.append(label, list);
  return {
    update(w, env) {
      const p = w.props as AnnouncementsProps;
      label.textContent = p.title || 'Announcements';
      label.style.display = p.title === '' ? 'none' : '';
      const items = (p.items ?? []).filter((a) => a.title || a.body);
      list.replaceChildren(...items.map((a) => {
        const it = el('div', 'anl-i'), tx = el('div', 'anl-tx');
        if (a.title) tx.append(el('div', 'ev-t anl-t', a.title));
        if (a.body) tx.append(el('div', 'ev-m anl-b', a.body));
        it.append(el('i', 'anl-dot'), tx);
        return it;
      }));
      if (!items.length && env.mode !== 'screen') list.append(el('div', 'nvd-sub nvd-faint', 'Add announcements in the inspector'));
    },
    tick() { return false; },
    destroy() {},
  };
}

/** Grid: each announcement on its own rounded tile, like the tiles in Apple's Shortcuts and Home apps. */
export function announcementsGrid(c: HTMLElement): WidgetView {
  const label = el('div', 'nvd-label ann-h lead'), grid = el('div', 'ang');
  c.append(label, grid);
  return {
    update(w, env) {
      const p = w.props as AnnouncementsProps;
      label.textContent = p.title || 'Announcements';
      label.style.display = p.title === '' ? 'none' : '';
      const items = (p.items ?? []).filter((a) => a.title || a.body);
      // Columns that keep the tiles closest to a comfortable card shape for this widget's size.
      const n = Math.max(1, items.length), W = env.box.width, H = Math.max(1, env.box.height * 0.82);
      let cols = 1, best = Infinity;
      for (let k = 1; k <= Math.min(n, 4); k++) { const r = Math.ceil(n / k), shape = (W / k) / (H / r), d = Math.abs(Math.log(shape / 1.7)) + (r * k - n) * 0.2; if (d < best) { best = d; cols = k; } }
      grid.style.setProperty('--ang-cols', String(cols));
      grid.replaceChildren(...items.map((a, k) => {
        const it = el('div', 'ang-i');
        // The last tile stretches across any space left in its row, so the grid never has a gap.
        const spare = (cols - (items.length % cols)) % cols;
        if (k === items.length - 1 && spare) it.style.gridColumn = `span ${spare + 1}`;
        if (a.title) it.append(el('div', 'ev-t ang-t', a.title));
        if (a.body) it.append(el('div', 'ev-m ang-b', a.body));
        return it;
      }));
      if (!items.length && env.mode !== 'screen') grid.append(el('div', 'nvd-sub nvd-faint', 'Add announcements in the inspector'));
    },
    tick() { return false; },
    destroy() {},
  };
}

/* ---------- Donation QR designs ---------- */

export function qrDesign(c: HTMLElement, design: 'side' | 'minimal', qrSvg: (t: string) => string): WidgetView {
  const wrap = el('div', design === 'side' ? 'qr-side' : 'qr-min');
  const box = el('div', 'qr'), text = el('div', 'qr-text'), label = el('div', 'nvd-label'), title = el('div', 'tx'), cap = el('div', 'nvd-sub');
  text.append(label, title, cap);
  wrap.append(box);
  if (design === 'side') wrap.append(text);
  c.append(wrap);
  let lastUrl = '';
  return {
    update(w, env) {
      wrap.classList.toggle('stack', design === 'side' && env.box.width / Math.max(1, env.box.height) < 1.2);
      const p = w.props as DonationQrProps;
      label.textContent = 'Scan to give';
      title.textContent = p.title || 'Donate';
      cap.textContent = p.caption || '';
      const url = /^https?:\/\//i.test(p.url) ? p.url : '';
      if (url !== lastUrl) { lastUrl = url; box.innerHTML = url ? qrSvg(url) : ''; }
    },
    tick() { return false; },
    destroy() {},
  };
}

/* ---------- Ticker designs ---------- */

export function tickerDesign(c: HTMLElement, design: 'plain' | 'pills'): WidgetView {
  const tk = el('div', `tk tk-${design}`), win = el('div', 'tk-win'), run = el('div', 'tk-run');
  win.append(run);
  tk.append(win);
  c.append(tk);
  c.style.padding = '0';
  let speed = 60, sig = '';
  const measure = () => {
    const w = [...run.children].slice(0, run.children.length / 2).reduce((s, n) => s + (n as HTMLElement).offsetWidth, 0);
    if (!w) return;
    run.style.setProperty('--tk-d', `${-w}px`);
    run.style.setProperty('--tk-t', `${(w / Math.max(10, speed * 2.2)).toFixed(2)}s`);
    run.classList.add('go');
  };
  return {
    update(w) {
      const p = w.props as TickerProps;
      speed = p.speed || 60;
      const texts = (p.items ?? []).map((i) => i.text).filter(Boolean);
      const s = texts.join('\u0000') + '|' + speed;
      if (s === sig) return;
      sig = s;
      run.classList.remove('go');
      const make = () => texts.map((t) => el('span', '', t));
      run.replaceChildren(...make(), ...make());
      requestAnimationFrame(measure);
    },
    tick() { return false; },
    destroy() { run.classList.remove('go'); },
  };
}

/* ---------- Ramadan: Iftar, Sehri ends, Tarawih ---------- */

/**
 * The next Iftar (Maghrib's start), end of Sehri or Tarawih, from the timetable. Shows the time large or as a
 * countdown, and the day of Ramadan while it's Ramadan. Sehri and Tarawih only appear in Ramadan, when the timetable
 * has those columns filled in.
 */
export function ramadanWidget(c: HTMLElement, kind: 'iftar' | 'sehri' | 'tarawih'): WidgetView {
  let p: RamadanProps, design = 'time';
  const wrap = el('div', 'nvd-center rm');
  const label = el('div', 'nvd-label'), big = el('div', 'big'), sub = el('div', 'nvd-sub'), day = el('div', 'nvd-sub nvd-acc');
  wrap.append(label, big, sub, day);
  c.append(wrap);
  let target: { day: string; time: string; at: Date } | null = null, key = '';
  const find = (env: Env, now: Date) => {
    const tz = env.masjid.timezone, today = ymd(now, tz);
    for (let i = 0; i < 60; i++) {
      const d = addDays(today, i);
      const row = env.timetable.get(d);
      const t = kind === 'iftar' ? row?.maghrib : kind === 'sehri' ? row?.sehri : row?.tarawih;
      if (!t) continue;
      const at = zonedToInstant(d, t, tz);
      // Keep showing it for a few minutes after (Iftar at sunset, Tarawih while it starts).
      if (at.getTime() > now.getTime() - 5 * 60_000) return { day: d, time: t, at };
    }
    return null;
  };
  return {
    update(w) { p = w.props as RamadanProps; design = designOf(w); label.textContent = p.title || WIDGET_TITLE[kind]; key = ''; target = null; },
    tick({ now, day: today }, env) {
      let ch = false;
      if (!target || target.at.getTime() < now.getTime() - 5 * 60_000 || key !== today) {
        key = today;
        target = find(env, now);
        ch = true;
        const h = hijriFor(today, env.masjid.prayer.hijriAdjust);
        const ram = h.month === 9;
        day.style.display = p.showDay && ram ? '' : 'none';
        setText(day, ram ? `Ramadan ${h.day}` : '');
      }
      if (!target) {
        big.style.display = '';
        ch = setText(big, '—') || ch;
        sub.style.display = '';
        return setText(sub, env.mode === 'screen' ? '' : kind === 'iftar' ? 'Add Maghrib times to your timetable' : `Add ${kind === 'sehri' ? 'Sehri Ends' : 'Tarawih'} times to your timetable for Ramadan`) || ch;
      }
      const left = target.at.getTime() - now.getTime();
      const when = target.day === today ? '' : target.day === addDays(today, 1) ? 'Tomorrow ' : `${formatDate(target.at, env.masjid.timezone, 'short')} · `;
      if (design === 'countdown') {
        ch = setText(big, left > 0 ? clockDuration(left) : 'Now') || ch;
        sub.style.display = '';
        ch = setText(sub, `${when}at ${wall12(target.time, p.h12)}`) || ch;
      } else {
        ch = setText(big, wall12(target.time, p.h12)) || ch;
        sub.style.display = p.showCountdown || when ? '' : 'none';
        ch = setText(sub, left <= 0 ? 'Now' : `${when}${p.showCountdown ? `in ${compactDuration(left)}` : ''}`.replace(/ · $/, '')) || ch;
      }
      return ch;
    },
    destroy() {},
  };
}
const WIDGET_TITLE = { iftar: 'Iftar', sehri: 'Sehri ends', tarawih: 'Tarawih' } as const;

/* ---------- Announcements: one urgent message ---------- */

/** A single message styled for its kind (the same look as a message sent live from the dashboard). */
export function alertDesign(c: HTMLElement): WidgetView {
  c.style.padding = '0';
  const root = el('div', 'nvd-bm nvd-alert'), inner = el('div', 'bm-in'), tag = el('div', 'bm-tag'), h = el('div', 'bm-h'), b = el('div', 'bm-b');
  inner.append(tag, h, b);
  root.append(inner);
  c.append(root);
  return {
    update(w, env) {
      const p = w.props as AnnouncementsProps;
      const a = p.alert ?? { preset: 'notice' as const, heading: 'Notice', body: '' };
      const pr = presetOf(a.preset);
      root.className = `nvd-bm nvd-alert k-${pr.id}${env.box.width / env.box.height > 4.2 ? ' wide' : ''}`;
      root.style.setProperty('--bm', pr.color);
      // The widget's colour replaces the kind's colour when one is chosen; text is the chosen text colour, or
      // black or white, whichever reads best on the message's colour.
      const bg = w.fill && w.style !== 'none' ? w.fill : pr.color;
      root.style.color = w.textColor || rgbToHex(autoInk(hexToRgb(bg)));
      tag.textContent = pr.label;
      h.textContent = a.heading || pr.heading;
      b.textContent = a.body;
      b.style.display = a.body ? '' : 'none';
    },
    tick() { return false; },
    destroy() {},
  };
}
